<template>
  <a-modal
    title="绑定广告"
    :visible="visible"
    :width="800"
    @ok="handleOk"
    @cancel="handleCancel"
    :confirmLoading="submitLoading"
  >
    <a-form-model ref="form" :model="form" :rules="rules" :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
      <a-form-model-item label="绑定范围" prop="bindType">
        <a-radio-group v-model="form.bindType" @change="handleBindTypeChange">
          <a-radio :value="0">全部</a-radio>
          <a-radio :value="1">选择剧集</a-radio>
        </a-radio-group>
      </a-form-model-item>

      <!-- 全部模式 -->
      <a-form-model-item
        v-if="form.bindType === 0"
        label="选择广告"
        prop="advertisementId"
      >
        <a-select
          v-model="form.advertisementId"
          placeholder="请选择广告"
          show-search
          :filter-option="filterAdvertisementOption"
          allow-clear
        >
          <a-select-option
            v-for="item in advertisementList"
            :key="item.id"
            :value="item.id"
          >
            {{ item.remark }}
          </a-select-option>
        </a-select>
      </a-form-model-item>

      <!-- 选择剧集模式 -->
      <a-form-model-item
        v-if="form.bindType === 1"
        label="剧集列表"
        prop="episodeBindings"
      >
        <div class="episode-list-container">
          <div
            v-for="(episode, index) in episodeList"
            :key="episode.id"
            class="episode-item"
          >
            <span class="episode-label">第{{ episode.serialNumber }}集</span>
            <a-select
              v-model="episodeBindings[getEpisodeKey(episode.id)]"
              placeholder="请选择广告"
              style="width: 300px; margin-left: 16px;"
              show-search
              :filter-option="filterAdvertisementOption"
              allow-clear
              @change="(value) => handleEpisodeAdChange(value, episode.id)"
            >
              <a-select-option
                v-for="item in advertisementList"
                :key="item.id"
                :value="item.id"
              >
                {{ item.remark }}
              </a-select-option>
            </a-select>
          </div>
          <div v-if="episodeList.length === 0" class="empty-tip">
            暂无剧集数据
          </div>
        </div>
      </a-form-model-item>
    </a-form-model>
  </a-modal>
</template>

<script>
import {listAdvertisement, bingGather,getGatherList} from '@/api/video/advertisement'
import { listGather } from '@/api/video/gather'

export default {
  name: 'BindAdvertisementForm',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    videoId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      submitLoading: false,
      advertisementList: [],
      episodeList: [],
      episodeBindings: {}, // 存储每个剧集对应的广告ID { gatherId: advertisementId }
      form: {
        bindType: 0, // 0-全部, 1-选择剧集
        advertisementId: undefined
      },
      rules: {
        advertisementId: [
          {
            validator: (rule, value, callback) => {
              if (this.form.bindType === 0 && !value) {
                callback(new Error('请选择广告'))
              } else {
                callback()
              }
            },
            trigger: 'change'
          }
        ],
        episodeBindings: [
          {
            validator: (rule, value, callback) => {
              if (this.form.bindType === 1) {
                const hasBinding = Object.values(this.episodeBindings).some(adId => adId)
                if (!hasBinding) {
                  callback(new Error('请至少为一个剧集选择广告'))
                } else {
                  callback()
                }
              } else {
                callback()
              }
            },
            trigger: 'change'
          }
        ]
      }
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.initForm()
        // 先获取广告列表，确保回显时能找到对应的广告项
        this.getAdvertisementList().then(() => {
          if (this.videoId) {
            this.getEpisodeList().then(() => {
              // 获取剧集列表后，再获取绑定数据
              this.getBindData()
            })
          }
        }).catch(() => {
          // 即使广告列表加载失败，也继续获取剧集列表
          if (this.videoId) {
            this.getEpisodeList().then(() => {
              this.getBindData()
            })
          }
        })
      }
    },
    videoId(val) {
      if (val && this.visible) {
        this.getEpisodeList().then(() => {
          this.getBindData()
        })
      }
    }
  },
  methods: {
    initForm() {
      this.form = {
        bindType: 0,
        advertisementId: undefined
      }
      this.episodeBindings = {}
      this.episodeList = []
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate()
        }
      })
    },
    /** 获取广告列表 */
    getAdvertisementList() {
      return listAdvertisement().then(response => {
        if (response.success) {
          this.advertisementList = response.data || []
          return Promise.resolve()
        } else {
          this.$message.error(response.message || '获取广告列表失败')
          return Promise.reject(response)
        }
      }).catch(error => {
        this.$message.error('获取广告列表失败')
        return Promise.reject(error)
      })
    },
    /** 获取剧集列表 */
    getEpisodeList() {
      return new Promise((resolve, reject) => {
        if (!this.videoId) {
          resolve()
          return
        }
        listGather({ videoId: this.videoId }).then(response => {
          if (response.success) {
            this.episodeList = response.data || []
            // 初始化剧集绑定对象
            this.episodeBindings = {}
            this.episodeList.forEach(episode => {
              // 统一使用数字类型作为key，确保类型一致
              const key = typeof episode.id === 'string' ? parseInt(episode.id) : episode.id
              this.$set(this.episodeBindings, key, undefined)
            })
            resolve()
          } else {
            this.$message.error(response.message || '获取剧集列表失败')
            reject(response)
          }
        }).catch(error => {
          this.$message.error('获取剧集列表失败')
          reject(error)
        })
      })
    },
    /** 获取绑定数据 */
    getBindData() {
      if (!this.videoId) {
        return
      }
      // 确保广告列表已加载后再获取绑定数据
      if (this.advertisementList.length === 0) {
        // 如果广告列表还没加载，先加载广告列表
        this.getAdvertisementList().then(() => {
          this.fetchBindData()
        })
      } else {
        this.fetchBindData()
      }
    },
    /** 获取绑定数据（内部方法） */
    fetchBindData() {
      getGatherList({ videoId: this.videoId }).then(response => {
        if (response.success) {
          // 即使data为空对象，也尝试回显
          if (response.data) {
            // 使用$nextTick确保在下一个tick中回显，确保episodeBindings已经初始化
            this.$nextTick(() => {
              this.setBindData(response.data)
            })
          }
        }
      }).catch(error => {
        // 不显示错误提示，因为可能是首次绑定，没有数据
      })
    },
    /** 设置绑定数据（回显） */
    setBindData(bindData) {
      if (!bindData) {
        return
      }

      // 判断绑定类型：如果有data数据，默认使用选择剧集模式；否则根据type判断
      let bindType = bindData.type
      if (bindType === undefined || bindType === null) {
        // type不返回时，如果有data数据，默认展示选择剧集
        if (bindData.data && bindData.data.trim()) {
          bindType = 1
        } else {
          bindType = 0
        }
      }

      // 设置绑定类型
      this.form.bindType = bindType

      if (bindType === 0) {
        // 全部模式：设置广告ID
        this.form.advertisementId = bindData.advertisementId
      } else if (bindType === 1) {
        // 选择剧集模式：解析 data 字段
        if (bindData.data && bindData.data.trim()) {
          // 确保episodeBindings已经初始化
          if (Object.keys(this.episodeBindings).length === 0 && this.episodeList.length > 0) {
            this.episodeList.forEach(episode => {
              // 统一使用数字类型作为key，确保类型一致
              const key = typeof episode.id === 'string' ? parseInt(episode.id) : episode.id
              this.$set(this.episodeBindings, key, undefined)
            })
          }

          // data 格式：gatherId-advertisementId,gatherId-advertisementId
          const bindings = bindData.data.split(',')
          bindings.forEach(binding => {
            const trimmedBinding = binding.trim()
            if (trimmedBinding) {
              const parts = trimmedBinding.split('-')
              if (parts.length === 2) {
                // 尝试多种类型匹配
                const gatherIdStr = parts[0].trim()
                const advertisementIdStr = parts[1].trim()
                const gatherId = parseInt(gatherIdStr)
                const advertisementId = parseInt(advertisementIdStr)

                if (gatherId && !isNaN(gatherId) && advertisementId && !isNaN(advertisementId)) {
                  // 统一使用数字类型作为key和value，确保与下拉选项的value类型一致
                  // 确保advertisementId的类型与advertisementList中的id类型一致
                  const adItem = this.advertisementList.find(ad => {
                    const adId = typeof ad.id === 'string' ? parseInt(ad.id) : ad.id
                    return adId === advertisementId
                  })
                  if (adItem) {
                    // 使用广告列表中的实际id值（保持原始类型）
                    const finalAdId = adItem.id
                    this.$set(this.episodeBindings, gatherId, finalAdId)
                  } else {
                    // 如果找不到匹配的广告，使用解析出的id（可能是类型问题）
                    this.$set(this.episodeBindings, gatherId, advertisementId)
                  }
                }
              }
            }
          })
        }
      }

      // 确保表单数据更新
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate()
        }
        // 强制更新视图
        this.$forceUpdate()
      })
    },
    /** 绑定类型变化 */
    handleBindTypeChange(e) {
      if (e.target.value === 0) {
        // 选择全部时，不清空剧集绑定，保留数据以便切换回来时恢复
        // this.episodeBindings = {}
      } else {
        // 选择剧集时，如果episodeBindings为空或缺少某些剧集，才初始化
        if (Object.keys(this.episodeBindings).length === 0) {
          // 如果完全没有数据，初始化绑定对象
          this.episodeList.forEach(episode => {
            const key = typeof episode.id === 'string' ? parseInt(episode.id) : episode.id
            this.$set(this.episodeBindings, key, undefined)
          })
        } else {
          // 如果有数据，确保所有剧集都在绑定对象中（新增的剧集需要初始化）
          this.episodeList.forEach(episode => {
            const key = typeof episode.id === 'string' ? parseInt(episode.id) : episode.id
            if (!(key in this.episodeBindings)) {
              this.$set(this.episodeBindings, key, undefined)
            }
          })
        }
      }
    },
    /** 剧集广告选择变化 */
    handleEpisodeAdChange(value, gatherId) {
      // 如果清空选择（value为undefined或null），确保更新绑定对象
      if (value === undefined || value === null) {
        // 直接清空对应剧集的绑定
        if (gatherId) {
          this.$set(this.episodeBindings, gatherId, undefined)
        }
      }
      // 触发验证
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.validateField('episodeBindings')
        }
      })
    },
    /** 获取剧集key（统一类型） */
    getEpisodeKey(episodeId) {
      // 统一转换为数字类型
      return typeof episodeId === 'string' ? parseInt(episodeId) : episodeId
    },
    /** 广告搜索过滤 */
    filterAdvertisementOption(input, option) {
      return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
    },
    /** 确定 */
    handleOk() {
      this.$refs.form.validate(valid => {
        if (valid) {
          this.submitLoading = true

          let formData = {}

          if (this.form.bindType === 0) {
            // 全部模式
            formData = {
              type: 0,
              advertisementId: this.form.advertisementId,
              videoId: this.videoId
            }
          } else {
            // 选择剧集模式
            // 构建 data 字符串：用逗号拼接的"剧集id-广告id"格式
            const dataArray = []
            Object.keys(this.episodeBindings).forEach(gatherId => {
              const advertisementId = this.episodeBindings[gatherId]
              if (advertisementId) {
                dataArray.push(`${gatherId}-${advertisementId}`)
              }
            })

            formData = {
              type: 1,
              videoId: this.videoId,
              data: dataArray.join(',')
            }
          }
          bingGather(formData).then(response => {
            if (response.success) {
              // this.$message.success('绑定成功', 3)
              this.$emit('ok', formData)
              this.handleCancel()
            } else {
              this.$message.error(response.message || '绑定失败')
            }
          }).catch(error => {
            this.$message.error('绑定失败，请重试')
          }).finally(() => {
            this.submitLoading = false
          })
        }
      })
    },
    /** 取消 */
    handleCancel() {
      this.$emit('cancel')
    }
  }
}
</script>

<style lang="less" scoped>
.episode-list-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 12px;
  background: #fafafa;
}

.episode-item {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding: 8px;
  background: #fff;
  border-radius: 4px;

  &:last-child {
    margin-bottom: 0;
  }
}

.episode-label {
  min-width: 80px;
  font-weight: 500;
  color: #333;
}

.empty-tip {
  text-align: center;
  color: #999;
  padding: 20px;
}
</style>
